import React, { useEffect, useState } from 'react';
import { Paper, Typography, Box, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import BoltIcon from '@mui/icons-material/BoltOutlined';

interface ActiveUnitsData {
  eaq: number;
  aq: number;
  breakdown: Array<{
    serviceType: string;
    totalUnits: number;
    count: number;
  }>;
}

interface ActiveUnitsProps {
  data: ActiveUnitsData | null;
  isLoading: boolean;
}

const ActiveUnits: React.FC<ActiveUnitsProps> = ({ data, isLoading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2, color: '#fff' }}>Loading active units...</Typography>
      </Paper>
    );
  }

  if (!data) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography sx={{ color: '#fff' }}>No active units data available</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <BoltIcon sx={{ color: '#fff', mr: 1 }} />
        <Typography variant="h6" sx={{ color: '#fff' }}>
          Active Units (Live Contracts Only)
        </Typography>
      </Box>

      <Box sx={{ height: isMobile ? 350 : 400 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={[
              { name: 'Electricity (EAQ)', value: data.eaq, color: '#10B981' },
              { name: 'Gas (AQ)', value: data.aq, color: '#F59E0B' },
              { name: 'Combined Total', value: data.eaq + data.aq, color: '#FFFFFF' },
            ]}
            margin={{ 
              top: 20, 
              right: isMobile ? 10 : 30, 
              left: isMobile ? 40 : isTablet ? 60 : 100, 
              bottom: isMobile ? 80 : 60 
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
            <XAxis 
              dataKey="name" 
              tick={{ fill: '#fff', fontSize: isMobile ? 10 : 13 }}
              stroke="rgba(255, 255, 255, 0.2)"
              angle={isMobile ? -35 : -15}
              textAnchor="end"
              height={isMobile ? 80 : 60}
            />
            <YAxis 
              tick={{ fill: '#fff', fontSize: isMobile ? 10 : 13 }}
              stroke="rgba(255, 255, 255, 0.2)"
              tickFormatter={(value) => isMobile ? `${(value / 1000).toFixed(0)}k` : value.toLocaleString()}
            />
            <Tooltip 
              contentStyle={{
                background: 'rgba(30, 41, 59, 0.95)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                color: 'rgba(255, 255, 255, 0.9)',
              }}
              formatter={(value: number) => [`${value.toLocaleString()} kWh`]}
              labelStyle={{ color: '#fff', fontWeight: 'bold', marginBottom: '4px' }}
              cursor={false}
            />
            <Bar dataKey="value" radius={[8, 8, 0, 0]}>
              {[
                { name: 'Electricity (EAQ)', value: data.eaq, color: '#10B981' },
                { name: 'Gas (AQ)', value: data.aq, color: '#F59E0B' },
                { name: 'Combined Total', value: data.eaq + data.aq, color: '#FFFFFF' },
              ].map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {/* Summary stats */}
      <Box sx={{ 
        mt: 3, 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 3, 
        justifyContent: 'center',
        alignItems: isMobile ? 'stretch' : 'center'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" sx={{ color: '#10B981', fontWeight: 'bold' }}>
            {data.eaq.toLocaleString()}
          </Typography>
          <Typography variant="caption" sx={{ color: '#fff' }}>
            Total EAQ (kWh)
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" sx={{ color: '#F59E0B', fontWeight: 'bold' }}>
            {data.aq.toLocaleString()}
          </Typography>
          <Typography variant="caption" sx={{ color: '#fff' }}>
            Total AQ (kWh)
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
            {(data.eaq + data.aq).toLocaleString()}
          </Typography>
          <Typography variant="caption" sx={{ color: '#fff' }}>
            Combined Total (kWh)
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default ActiveUnits;
